import { Box, useMediaQuery, useTheme } from "@mui/material";
import { lazy, Suspense, useLayoutEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { OfflineConnectionBar, OfflineIndicator, useOfflineBarVariant } from "components/offlineIndicator";
import {
    MINIMAP_LAYOUT,
    MobileToolbars,
    OFFLINE_INDICATOR_LAYOUT,
    PROCORE_TOP_BAR,
    TOOLBAR_LAYOUT,
} from "components/toolbars";
import { UndoRedoStackVisualizer } from "components/undoRedoStackVisualizer";
import { useCoreWindowRegistration } from "components/window";
import { useIsPhone, useIsPhonePortrait } from "contexts/panelMode";
import { VISIBILITY_TOOLBAR_IDS } from "contexts/toolbars";
import { AxisSphere } from "features/axisSphere/AxisSphere";
import { LargeBookmarkWarningDialog } from "features/bookmarks/largeBookmarkWarningDialog";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { DetailsMenu } from "features/detailsMenu/detailsMenu";
import { GlobalSearch } from "features/globalSearch/globalSearch";
import { Joysticks } from "features/joysticks/joysticks";
import LocationHud from "features/locationHud/locationHud";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";
import { PinOffsetCursor } from "features/markers/PinOffsetCursor";
import { MiniMapMobile, MiniMapWeb } from "features/miniMap";
import { MeasurementOverlayButtons, MobileMeasureCursor, MobileMeasureIntegration } from "features/mobileMeasure";
import { NavigationCube } from "features/navigationCube";
import { IncrementalLoadingPopup } from "features/offline/incrementalLoadingPopup";
import { PickerEscSnackbar } from "features/pickerEscSnackbar/pickerEscSnackbar";
import { PrimaryMenu, PrimaryMenuNew } from "features/primaryMenu";
import { BottomNavToolbar } from "features/primaryMenu/BottomNavToolbar";
import { QuickAccessMenu as ProcoreQuickAccessMenu } from "features/quickAccessMenu/procore/quickAccessMenu";
import { QuickAccessMenu } from "features/quickAccessMenu/quickAccessMenu";
import { QuirkAlert } from "features/quirkAlert";
import { ReleaseCandidateToggler } from "features/releaseCandidateToggler";
import { selectNavigationCube } from "features/render";
import { SelectionModifierMenu } from "features/selectionModifierMenu";
import { WidgetGroupPanel } from "features/widgetGroupPanel/widgetGroupPanel";
import { Widgets } from "features/widgets";
import { useHandleWidgetLayout } from "features/widgets/useHandleWidgetLayout";
import {
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsProcoreDesign,
    selectIsProcoreMobileDesign,
    selectNewDesign,
    selectWidgetLayout,
} from "slices/explorer";
import { selectIsFocusedToolMode } from "slices/procoreUx";
import { selectIsToolbarVisible } from "slices/toolbarVisibility";

import { AdaptivePanelContainer } from "./AdaptivePanelContainer";
import { CameraIsTooFarMessage } from "./cameraIsTooFarMessage";
import { CoachmarkDisplay } from "./coachmarkDisplay";
import GlobalSnackbar from "./globalSnackbar";
import NewProjectVersionSnackbar from "./newProjectVersionSnackbar";
import SceneBackfillSnackbar from "./sceneBackfillSnackbar";
import { UnusuallyFarObjectsMessage } from "./unusuallyFarObjectsMessage";
import WeakDeviceSnackbar from "./weakDeviceSnackbar";

const LazyNodeClassTreePanel = import.meta.env.DEV
    ? lazy(() =>
          import("features/nodeClassDebug").then(({ NodeClassTreePanel }) => ({
              default: NodeClassTreePanel,
          })),
      )
    : undefined;

function NodeClassDebugPanel() {
    return LazyNodeClassTreePanel ? (
        <Suspense fallback={null}>
            <LazyNodeClassTreePanel />
        </Suspense>
    ) : null;
}

export function Hud() {
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);

    if (isProcoreDesign) {
        if (isProcoreMobileDesign) {
            return <HudProcoreMobile />;
        } else {
            return <HudProcore />;
        }
    }

    if (newDesign) {
        return (
            <>
                <HudNew />
                <IncrementalLoadingPopup />
                <QuirkAlert />
            </>
        );
    }

    return (
        <>
            <HudOld />
            <IncrementalLoadingPopup />
            <QuirkAlert />
        </>
    );
}

function HudNew() {
    const navigationCube = useAppSelector(selectNavigationCube);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    useHandleWidgetLayout();

    return (
        <>
            <LargeBookmarkWarningDialog />
            {navigationCube.enabled && <NavigationCube />}
            <Box
                sx={{
                    position: "absolute",
                    inset: theme.spacing(
                        isSmall ? theme.customSpacing.hudPaddingMobile : theme.customSpacing.hudPadding,
                    ),
                    pointerEvents: "none",
                }}
            >
                <LocationHud />
                <QuickAccessMenu />
                {!isSmall && <WidgetGroupPanel />}
                <Widgets />
                <GlobalSnackbar />
                <CameraIsTooFarMessage />
                <UnusuallyFarObjectsMessage />
                <PickerEscSnackbar />
                <WeakDeviceSnackbar />
                <NewProjectVersionSnackbar />
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    inset: isSmall ? 0 : theme.spacing(theme.customSpacing.hudPadding),
                    pointerEvents: "none",
                }}
            >
                <PrimaryMenuNew />
            </Box>
            <GlobalSearch />
            <NodeClassDebugPanel />
        </>
    );
}

function HudProcore() {
    useCoreWindowRegistration();
    const quickAccessVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.QUICK_ACCESS));
    const detailsVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.DETAILS));
    const navWheelVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.NAV_WHEEL));
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);

    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                }}
            >
                <Box sx={{ display: quickAccessVisible ? undefined : "none" }}>
                    <ProcoreQuickAccessMenu />
                </Box>
                {detailsVisible && <DetailsMenu />}
                {navWheelVisible && <BottomNavToolbar />}
                {!quickAccessVisible && isAxisSphereVisible && (
                    <Box sx={{ position: "absolute", right: "16px", top: "16px" }}>
                        <AxisSphere />
                    </Box>
                )}
                <Box sx={{ display: isMiniMapVisible ? "contents" : "none" }}>
                    <MiniMapWeb />
                </Box>
                <CoachmarkDisplay />
                <PinOffsetCursor />
                <GlobalSnackbar />
                <SceneBackfillSnackbar />
                <ReleaseCandidateToggler />
                <UndoRedoStackVisualizer />
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        pointerEvents: "none",
                        zIndex: 1200,
                    }}
                >
                    <OfflineIndicator />
                </Box>
            </Box>
            <NodeClassDebugPanel />
        </>
    );
}

function HudProcoreMobile() {
    useCoreWindowRegistration();
    const isPhone = useIsPhone();
    const isPhonePortrait = useIsPhonePortrait();
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible);
    // Section and measure need the whole small screen, so the navigation overlays
    // step aside. The axis sphere and mini map are hidden rather than unmounted
    // because the former owns a WebGL context and the latter holds downloaded
    // drawing data, and users toggle in and out of these modes constantly.
    const hideNavigationOverlays = useAppSelector(selectIsFocusedToolMode);
    const topBarHeight = isPhonePortrait ? PROCORE_TOP_BAR.HEIGHT_PORTRAIT : PROCORE_TOP_BAR.HEIGHT;
    const offlineBarVariant = useOfflineBarVariant();
    // On mobile both no-connection bars sit below the in-app header. The
    // full-width "intermittent" bar additionally pushes the content below it down.
    const showMobileOfflineBar = offlineBarVariant === "intermittent";

    // Anchor the no-connection bars flush to the actual rendered header, whose
    // height varies with safe-area insets and the portrait dropdown layout (the
    // topBarHeight constant is an upper bound and leaves a gap).
    const [headerHeight, setHeaderHeight] = useState<number>(topBarHeight);
    useLayoutEffect(() => {
        const el = document.getElementById(ELEMENT_IDS.mobileTopBar);
        if (!el) {
            return;
        }
        const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
        observer.observe(el);
        setHeaderHeight(el.offsetHeight);
        return () => observer.disconnect();
    }, [topBarHeight]);

    // The bar wraps to multiple lines on narrow screens, so measure its actual
    // height instead of assuming a single-line height to keep content clear of it.
    const offlineBarRef = useRef<HTMLDivElement | null>(null);
    const [offlineBarHeight, setOfflineBarHeight] = useState<number>(OFFLINE_INDICATOR_LAYOUT.HEIGHT);
    useLayoutEffect(() => {
        const el = offlineBarRef.current;
        if (!showMobileOfflineBar || !el) {
            return;
        }
        const observer = new ResizeObserver(() => setOfflineBarHeight(el.offsetHeight));
        observer.observe(el);
        setOfflineBarHeight(el.offsetHeight);
        return () => observer.disconnect();
    }, [showMobileOfflineBar]);
    // When the bar is shown, content sits below the measured header + bar so it
    // stays flush; otherwise it falls back to the topBarHeight constant.
    const contentTopOffset = showMobileOfflineBar ? headerHeight + offlineBarHeight : topBarHeight;

    return (
        <>
            <Box
                id="mobile-hud-container"
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Core mobile components */}
                <MobileToolbars.TopBar />

                {/* Offline chip ("as today"): below the header on mobile. */}
                <Box
                    sx={{
                        position: "absolute",
                        top: `${headerHeight}px`,
                        left: "50%",
                        transform: "translateX(-50%)",
                        pointerEvents: "none",
                        zIndex: 1200,
                    }}
                >
                    <OfflineIndicator />
                </Box>

                {/* Full-width "intermittent" bar: below the header, shifts content down. */}
                {showMobileOfflineBar && (
                    <Box
                        ref={offlineBarRef}
                        sx={{
                            position: "absolute",
                            top: `${headerHeight}px`,
                            left: 0,
                            right: 0,
                            pointerEvents: "none",
                            zIndex: 1200,
                        }}
                    >
                        <OfflineConnectionBar />
                    </Box>
                )}

                {isAxisSphereVisible && (
                    <Box
                        sx={{
                            position: "absolute",
                            right: `${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px`,
                            top: `${contentTopOffset + (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px`,
                            zIndex: MINIMAP_LAYOUT.Z_INDEX,
                            display: hideNavigationOverlays ? "none" : undefined,
                        }}
                    >
                        <AxisSphere size={isPhone ? 100 : 130} />
                    </Box>
                )}

                {/* MiniMap - right below top bar, portrait only */}

                <Box
                    sx={{
                        mt: `${contentTopOffset + (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px`,
                        ml: `max(${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px, env(safe-area-inset-left, 0px))`,
                        mr: `max(${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px, env(safe-area-inset-right, 0px))`,
                        zIndex: MINIMAP_LAYOUT.Z_INDEX,
                        display: hideNavigationOverlays ? "none" : undefined,
                    }}
                >
                    <MiniMapMobile />
                </Box>

                {/* Spacer to push joysticks to bottom */}
                <Box flex={1} sx={{ pointerEvents: "none" }} />

                {/* Joysticks - just above bottom toolbars. Unmounted rather than hidden
                    so the device-orientation listener and camera update loop stop with
                    them; otherwise "look around" would keep steering the camera with its
                    own toggle out of reach. */}
                {isJoysticksVisible && !hideNavigationOverlays && <Joysticks />}

                <CoachmarkDisplay
                    topOffset={`${(isPhonePortrait ? PROCORE_TOP_BAR.HEIGHT_PORTRAIT : PROCORE_TOP_BAR.HEIGHT) + 12}px`}
                />
                <MobileToolbars.BottomToolbar />
                <MobileToolbars.UndoRedoToolbar />
                <Box
                    sx={{
                        visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                        pointerEvents: nonMarkerUIIsHidden ? "none" : undefined,
                    }}
                >
                    <AdaptivePanelContainer />
                </Box>

                {/* Mobile measurement cursor and integration */}
                <PinOffsetCursor />
                <MobileMeasureCursor />
                <MobileMeasureIntegration />
                <MeasurementOverlayButtons />

                {/* Essential overlays */}
                <GlobalSnackbar />
                <SceneBackfillSnackbar />
            </Box>
        </>
    );
}

function HudOld() {
    const navigationCube = useAppSelector(selectNavigationCube);

    useHandleWidgetLayout();
    const layout = useAppSelector(selectWidgetLayout);

    const getGridLayout = () => {
        if (layout.widgets === 4) {
            return {
                gridTemplateColumns: "1fr 1fr repeat(2, minmax(420px, 1fr))",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        } else if (layout.widgets === 2) {
            return {
                gridTemplateColumns: "1fr 1fr minmax(420px, 1fr)",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        } else if (layout.widgets === 1 && layout.sideBySide) {
            return {
                gridTemplateColumns: "1fr 1fr minmax(420px, 1fr)",
                gridTemplateRows: "repeat(2, 1fr)",
            };
        } else if (layout.widgets === 1 && !layout.sideBySide) {
            return {
                gridTemplateColumns: "1fr 1fr 1fr",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        }
    };

    return (
        <>
            <LargeBookmarkWarningDialog />
            {navigationCube.enabled && <NavigationCube />}
            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                padding={2}
                display="grid"
                sx={{
                    pointerEvents: "none",
                    ...getGridLayout(),
                }}
            >
                <SelectionModifierMenu />
                <PrimaryMenu />
                <Widgets />
                <PickerEscSnackbar />
                <WeakDeviceSnackbar />
                <NewProjectVersionSnackbar />
            </Box>
            <NodeClassDebugPanel />
        </>
    );
}
